<template>
  <div
    v-if="pageLoading"
    class="m-3"
  >
    <placeholder-content :is-load="true" />
  </div>
  <div
    v-else-if="!hasPermission"
    class="m-3"
  >
    <access-denied/>
  </div>
  <div
    v-else-if="!promotionEnabled"
    class="m-3"
  >
    <div class="card">
      <div class="card-body text-center py-5">
        <i class="fa-solid fa-tags text-success fa-2x mb-3" />
        <div class="fw-semibold mb-1">{{ $t('_promotion.Promotion disabled') }}</div>
        <div class="text-muted mb-3" style="font-size: 13px;">{{ $t('Enable it in settings to manage promotions.') }}</div>
        <router-link to="/settings" class="btn btn-primary btn-sm">
          <i class="fa-solid fa-gear me-2" />{{ $t('_promotion.Go to Promotion Settings') }}
        </router-link>
      </div>
    </div>
  </div>
  <div
    v-else
  >
    <div class="d-flex justify-content-between border-bottom mb-3">
      <ul class="nav nav-custom">
        <li class="nav-item">
          <a class="nav-link active">
            {{ $t('All') }}
            <span class="badge rounded-pill bg-primary">
              {{ datatable.total }}
            </span>
          </a>
        </li>
      </ul>
      <column-toggle
        :headers="allHeaders"
        storage-key="promotions.columns"
        v-model="visibleColumns"
      />
    </div>
    <div class="d-flex justify-content-start align-items-center mx-3 flex-column flex-sm-row">
      <div class="btn-group btn-group-sm me-sm-2 mb-3">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click="() => {
            offcanvasFroms.open = true;
            offcanvasFroms.promotionId = 0;
            offcanvasFroms.type = 'CREATE';
          }"
        >
          <i class="fa-solid fa-circle-plus me-2" />
          {{ $t('Create') }}
        </button>
      </div>

      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          aria-label="Search"
          v-model.trim="params.search"
          @keyup.enter="fetchData"
        >
        <button
          class="input-group-text"
          type="button"
          @click="fetchData"
        >
          <i class="fas fa-search" />
        </button>
      </div>
      <per-page
        @per-page="setPerPage"
      />
    </div>
    <vue3-easy-data-table
      v-model:server-options="datatable.serverOptions"
      ref="dataTable"
      :headers="visibleHeaders"
      :items="datatable.items"
      :loading="datatable.loading"
      :server-items-length="datatable.total"
      :theme-color="'#515151'"
      :hide-footer="false"
      :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')"
      :table-min-height="null"
      :body-item-class-name="'align-top py-1 px-2'"
      buttons-pagination
      table-class-name='customize-table'
    >
      <template #item-tier_mode="item">
        {{ item.tier_mode ? $t('_promotion.tier_mode_' + item.tier_mode) : '-' }}
      </template>
      <template #item-is_active="item">
        <i v-if="item.is_active" class="fa-solid fa-check text-success" />
        <span v-else>-</span>
      </template>
      <template #item-start_date="item">
        {{ item.start_date ? String(item.start_date).slice(0, 10) : '-' }}
      </template>
      <template #item-end_date="item">
        {{ item.end_date ? String(item.end_date).slice(0, 10) : '-' }}
      </template>
      <template #item-action="item">
        <div class="icon-link custom-actions">
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => {
                offcanvasFroms.promotionId = item.id;
                offcanvasFroms.type = 'EDIT';
                offcanvasFroms.open = true;
              }"
            >
              <i class="fa-solid fa-square-pen" />
            </button>
            <template #content>
              <div>{{ $t('Edit') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="handleDelete(item.id)"
            >
              <i class="fa-solid fa-trash-can" />
            </button>
            <template #content>
              <div>{{ $t('Delete') }}</div>
            </template>
          </popper>
        </div>
      </template>
      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>
    <offcanvas-form
      v-if="offcanvasFroms.open"
      :type="offcanvasFroms.type"
      :promotion-id="offcanvasFroms.promotionId"
    />
  </div>
</template>
<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import PerPage from '@/plugins/PerPage.vue';
import FieldsDef from "./assets/template/datatable/FieldsDef";
import OffcanvasForm from './OffcanvasForm.vue';
import ColumnToggle from '@/components/shared/ColumnToggle.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import { loadVisibleColumns } from '@/utils/columnToggle';
import { Promotion } from '@/services/endpoint/promotion.service';
import Popper from "vue3-popper";

export default {
  data() {
    return {
      params: {
        limit: 10,
        page: 1,
        search: ''
      },
      pageLoading: false,
      hasPermission: true,
      promotionEnabled: true,
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10
        }
      },
      offcanvasFroms: {
        open: false,
        type: 'CREATE',
        promotionId: 0
      },
      visibleColumns: loadVisibleColumns('promotions.columns', FieldsDef(t => t))
    }
  },
  components: {
    PlaceholderContent,
    PerPage,
    Vue3EasyDataTable,
    OffcanvasForm,
    ColumnToggle,
    AccessDenied,
    Popper
  },
  computed: {
    allHeaders() {
      return this.datatable.headers(this.$t);
    },
    visibleHeaders() {
      return this.allHeaders.filter(h => !h.togglable || this.visibleColumns.includes(h.value));
    }
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    async fetchData() {
      this.datatable.loading = true;
      var queryString = '?' + $.param(this.params);
      await Promotion.list(queryString).then(res => {
        this.datatable.items = res.data.data;
        this.datatable.total = res.data.meta.total;
        this.promotionEnabled = !!res.data.is_enabled;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
      });

      this.datatable.loading = false;
      return;
    },
    async handleDelete(id) {
      try {
        let self = this;
        let title = this.$t("_promotion.Delete Promotion") + '?';
        let html = this.$t("You will not be able to undo this action.");
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return Promotion.delete(id)
              .then((response) => {
                self.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('_promotion.Delete Promotion Success!'));
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
    setPerPage(perPage) {
      this.params.limit = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
      this.fetchData();
    },
  },
  watch: {
    "datatable.serverOptions" (nval, oval) {
      delete this.params.sort;
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      if (nval.sortBy && nval.sortType) this.params.sort = nval.sortBy + "|" + nval.sortType;
      this.fetchData();
    }
  }
}
</script>
